import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import ResultBox from '../organisms/ResultBox'
import { setText, calculateStats, clearStats } from '../store/slices/wordCounterSlice'
import { useDebounce } from '../hooks/useDebounce'
import './WordCounterPage.css'

const WordCounterPage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const { text, stats } = useSelector(state => state.wordCounter)
  const debouncedText = useDebounce(text, 500)
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  const handleTextChange = (e) => {
    dispatch(setText(e.target.value))
  }

  const handleClear = () => {
    dispatch(setText(''))
    dispatch(clearStats())
  }

  useEffect(() => {
    if (debouncedText.trim()) {
      dispatch(calculateStats())
    } else {
      dispatch(clearStats())
    }
  }, [debouncedText, dispatch])


  return (
    <ToolPageTemplate path="/word-counter/" tools={tools}>
      <div>
        <section className="word-counter">
          <div className="word-input-section">
            <Input
              as="textarea"
              label="Enter your text"
              value={text}
              onChange={handleTextChange}
              placeholder="Start typing or paste your text here..."
              rows={8}
              style={{ resize: 'vertical' }}
            />
            <div className="word-info">
              <span className="word-char-count">
                {text.length} characters
              </span>
              <button className="word-clear-btn" onClick={handleClear}>
                Clear
              </button>
            </div>
          </div>

          {stats && (
            <div className="word-results">
              <ResultBox title="Text Statistics">
                <div className="word-stats">
                  <div className="stat-grid">
                    <div className="stat-item">
                      <span className="stat-number">{stats.words.toLocaleString()}</span>
                      <span className="stat-label">Words</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.characters.toLocaleString()}</span>
                      <span className="stat-label">Characters</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.charactersNoSpaces.toLocaleString()}</span>
                      <span className="stat-label">No Spaces</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.sentences}</span>
                      <span className="stat-label">Sentences</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.paragraphs}</span>
                      <span className="stat-label">Paragraphs</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.readingTime} min</span>
                      <span className="stat-label">Reading Time</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-number">{stats.speakingTime} min</span>
                      <span className="stat-label">Speaking Time</span>
                    </div>
                  </div>
                </div>
              </ResultBox>

              {stats.keywordDensity.length > 0 && (
                <ResultBox title="Top 10 Keywords">
                  <div className="keyword-density">
                    <div className="keyword-list">
                      {stats.keywordDensity.map((keyword, index) => (
                        <div key={keyword.word} className="keyword-item">
                          <span className="keyword-rank">#{index + 1}</span>
                          <span className="keyword-word">{keyword.word}</span>
                          <span className="keyword-count">{keyword.count}</span>
                          <span className="keyword-percentage">{keyword.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </ResultBox>
              )}
            </div>
          )}
        </section>

        <section className="tool-guide">
          <div className="guide-section">
            <h2>How to Use Word Counter</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Enter or Paste Your Text</h3>
                  <p>Type directly into the text area or paste your content from another source. The text area supports large amounts of content and automatically adjusts as you type. You can resize the text area vertically for better visibility of longer documents.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Automatic Real-Time Analysis</h3>
                  <p>As you type or paste content, the word counter instantly analyzes your text and updates all statistics in real-time. There's no need to click any buttons - the analysis happens automatically with a 500ms delay to ensure accurate results without constant updates.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Review Comprehensive Statistics</h3>
                  <p>Examine detailed text statistics including word count, character count (with and without spaces), sentence count, paragraph count, estimated reading time, and speaking time. The keyword density analysis shows your top 10 most frequently used words with their frequency percentages.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Optimize Your Content</h3>
                  <p>Use the keyword density analysis to optimize your content for SEO and readability. The reading time estimates help you create appropriately sized content for your target audience, while the character counts help you meet platform-specific requirements.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Why Use This Tool</h2>
            <p>Our word counter provides comprehensive text analysis that goes beyond simple word counting. It offers detailed insights into your content structure, readability, and keyword usage, making it an essential tool for content creators, students, and professionals. The real-time analysis and keyword density features help you optimize content for both human readers and search engines.</p>
            <p>The tool calculates reading and speaking time estimates based on average reading speeds, helping you create content that matches your audience's attention span. The character count with and without spaces is crucial for social media posts, SMS messages, and form submissions with character limits. Unlike basic counters, our tool provides actionable insights that improve content quality and effectiveness.</p>
          </div>

          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>What statistics does the word counter show?</h3>
                <p>Word count, character count (with and without spaces), sentence count, paragraph count, estimated reading time, estimated speaking time, and the top 10 keywords by frequency.</p>
              </div>
              <div className="faq-item">
                <h3>Does the word counter count characters with or without spaces?</h3>
                <p>Both. The tool shows character count including spaces and excluding spaces, so you can meet any platform limit — Twitter (280), SMS (160), meta descriptions (160), or essay guidelines.</p>
              </div>
              <div className="faq-item">
                <h3>How does keyword density analysis work?</h3>
                <p>It identifies the 10 most frequently used words in your text and shows their count and percentage of total words. Useful for SEO optimization and spotting keyword stuffing before publishing.</p>
              </div>
              <div className="faq-item">
                <h3>How accurate are the reading and speaking time estimates?</h3>
                <p>Reading time uses 200–250 words per minute; speaking time uses 150–160 words per minute. Both are industry-standard benchmarks used by publishers and content creators.</p>
              </div>
              <div className="faq-item">
                <h3>What is a good SEO word count for a blog post?</h3>
                <p>Most blog topics rank well at 1,200–2,000 words. Competitive niches often need 2,000+ words to outrank incumbents. Short-form content (300–600 words) can win for specific long-tail questions.</p>
              </div>
              <div className="faq-item">
                <h3>Is my text stored anywhere?</h3>
                <p>No. All analysis runs locally in your browser using JavaScript. Your text is never uploaded, logged, or stored on any server.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/age/" className="related-tool">
                <div className="tool-icon">Age Calculator</div>
                <div className="tool-description">Calculate exact age with zodiac signs and birthday countdown</div>
              </Link>
              <Link to="/text-to-speech/" className="related-tool">
                <div className="tool-icon">Text to Speech</div>
                <div className="tool-description">Convert your text to natural speech with voice controls</div>
              </Link>
              <Link to="/json-formatter/" className="related-tool">
                <div className="tool-icon">JSON Formatter</div>
                <div className="tool-description">Format, validate, and beautify JSON data structures</div>
              </Link>
              <Link to="/percentage/" className="related-tool">
                <div className="tool-icon">Percentage Calculator</div>
                <div className="tool-description">Calculate percentages and percentage changes</div>
              </Link>
              <Link to="/blog/how-to-count-words-effectively-for-seo-writing/" className="related-tool">
                <div className="tool-icon">Counting Words Effectively for SEO</div>
                <div className="tool-description">Optimal word counts and content strategy by page type</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default WordCounterPage